import React, { useEffect } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/react-hooks';
import DetailedTable from '../../components/DetailedTable';
import _ from 'lodash';
import { PROFILE_CONFIG_LIST, CREATE_PROFILE, DELETE_PROFILE_CONFIG, WORKFLOW_LIST } from '../graphQL/queries';
import { Button, Dialog, Typography, IconButton, Grid } from '@material-ui/core';
import ScrollIntoView from 'react-scroll-into-view';
import { DialogForm } from '../../components/DialogForm';
import columns from './Columns';
import { Edit, Trash, MoreVertical } from '@jda/lui-common-icon-library';
import { Loading } from '../../Loading';
import { getSimulationFields } from '../../utils/helpers';
import useStyles from './style';
import X from '@jda/lui-common-icon-library/ui/X';
import { useSnackbar } from 'notistack';
import AddProfile from './AddProfile';
import { workflowInfo, workflowObj } from '../../utils/interface';

interface ProfileProps {
  participant: any;
}

const Profile = (props: ProfileProps) => {
  const { participantCode } = props.participant;
  const workflowList = useQuery<workflowObj, {}>(WORKFLOW_LIST, {});
  const [getProfiles, { data: profileConfigList, loading }] = useLazyQuery(PROFILE_CONFIG_LIST, {
    variables: {
      method: 'GET',
      search: `?search=participantCode=${participantCode}`
    },
    fetchPolicy: 'network-only'
  });

  const [createProfile] = useMutation(CREATE_PROFILE);
  const [deleteProfile] = useMutation(DELETE_PROFILE_CONFIG);

  const [dataConfig, setDataConfig] = React.useState<any>([]);
  const [isEdit, setIsEdit] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const [rowData, setRowData] = React.useState<any>({ participantCode: '' });
  const [deleteRow, setDeleteRow] = React.useState<any>();
  const [workflowCode, setWorkflowCode] = React.useState<any>('');
  const [subService, setSubService] = React.useState<any>('');
  const [error, setError] = React.useState<any>({ service: '', subService: '' });
  const [selected, setSelected] = React.useState<any>(0);

  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  useEffect(() => {
    getProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profileActions: any = [
    {
      icon: () => (
        <Button variant="outlined" data-id="add-config" color="primary" id="add-config" onClick={addProfileConfig}>
          Add Profile
        </Button>
      ),
      position: 'toolbar'
    },
    {
      icon: () => <MoreVertical data-id="view-more" id="view-more" />,
      onClick: (event: any, rowData: any) => showMoreFields(rowData),
      tooltip: 'Show More',
      position: 'row'
    },
    {
      icon: () => <Edit data-id="edit-profile-config" />,
      onClick: (event: any, rowData: any) => editProfileConfig(rowData),
      tooltip: 'Edit User',
      position: 'row'
    },
    {
      icon: () => <Trash data-id="delete-profile-config" />,
      onClick: (event: any, rowData: any) => deleteConfig(rowData),
      tooltip: 'Delete User',
      position: 'row'
    }
  ];

  const addProfileConfig = () => {
    setDialogOpen(!dialogOpen);
    const service = workflowList.data?.workflow?.content[0]?.workflowCode;
    const profileConfigData = getDataConfig(service);
    setWorkflowCode(service);
    setDataConfig(getSimulationFields(profileConfigData?.[0]?.profileConfig, false));
  };

  const editProfileConfig = (rowData: any) => {
    const profileConfigData = getDataConfig(rowData?.service);
    const config: any = getSimulationFields(profileConfigData?.[0]?.profileConfig, true)?.map((data: any) => {
      const key = data.name || data?.key;
      const value = rowData[key];
      if (data?.multiple) {
        data.defaultValue = value?.split(',');
      } else data.defaultValue = value;
      return data;
    });
    setSubService(rowData?.subService);
    setWorkflowCode(rowData?.service);
    setIsEdit(!isEdit);
    setDialogOpen(!dialogOpen);
    setDataConfig(config);
  };

  const deleteConfig = (rowData: any) => {
    setDeleteRow(rowData);
    setIsDeleteOpen(!isDeleteOpen);
  };

  const deleteProfileConfig = () => {
    const payload = [
      {
        participantCode: deleteRow?.participantCode,
        service: deleteRow?.service,
        subService: deleteRow?.subService,
        operationRegion: deleteRow?.operationRegion,
        modes: deleteRow?.modes,
        responseType: deleteRow?.responseType
      }
    ];

    deleteProfile({
      variables: {
        payload
      }
    }).then(res => {
      enqueueSnackbar('Profile Config deleted successfully !!', {
        variant: 'success',
        preventDuplicate: true
      });
      resetForm();
      getProfiles();
    });
  };

  const showMoreFields = (rowData: any) => {
    setDialogOpen(!dialogOpen);
    setWorkflowCode(rowData?.service);
    setRowData(rowData);
    setIsEdit(!isEdit);
  };

  const handleProfileSubmit = (values: any) => {
    if (workflowCode === '') {
      setError({ ...error, ...{ service: 'Service is required' } });
      return;
    } else {
      setError({ ...error, service: '' });
    }
    if (subService === '') {
      setError({ ...error, ...{ subService: 'Sub Service is required' } });
      return;
    } else {
      setError({ ...error, subService: '' });
    }
    const participant = {
      participantCode: rowData?.participantCode || participantCode,
      service: workflowCode,
      operationRegion: values?.operationRegion?.toString(),
      subService
    };
    const payload = { ...values, ...participant };
    createProfile({
      variables: {
        payload,
        method: isEdit ? 'PUT' : 'POST'
      }
    })
      .then(res => {
        enqueueSnackbar('Profile Config created successfully !!', {
          variant: 'success',
          preventDuplicate: true
        });
        getProfiles();
        resetForm();
      })
      .catch(e => {
        enqueueSnackbar(error?.networkError?.result?.errors?.[0]?.userMessage || 'Profile Config execution failed !!', {
          variant: 'error',
          preventDuplicate: true
        });
      });
  };

  const getLatestValues = (dataObj: any) => {
    const editData: any[] = [];
    (dataConfig || []).forEach((obj: any) => {
      let temp = { ...obj };
      if (temp.type === 'boolean') {
        temp.defaultValue = Boolean(dataObj[temp.path]);
      } else if (temp.type === 'multiSelect') {
        temp.defaultValue = dataObj[temp.path];
      }
      editData.push(temp);
    });
    setDataConfig(editData);
  };

  const resetForm = () => {
    setDialogOpen(false);
    setRowData({ participantCode: '' });
    setIsDeleteOpen(false);
    setDeleteRow({});
    setIsEdit(false);
    setWorkflowCode('');
    setError({ service: '', subService: '' });
    setDataConfig([]);
    setSubService('');
  };

  const handleChangeWorkflow = (e: any) => {
    setError({ ...error, ...{ service: '' } });
    setDataConfig([]);
    setWorkflowCode(e?.target?.value);
    const profileConfigData = getDataConfig(e?.target?.value);
    if (profileConfigData?.[0]?.profileConfig)
      setDataConfig(getSimulationFields(profileConfigData?.[0]?.profileConfig, isEdit));
  };
  const handleChangeSubService = (e: any) => {
    setError({ ...error, ...{ subService: '' } });
    setSubService(e?.target?.value);
  };

  const getDataConfig = (workflowCode: any) =>
    workflowList.data?.workflow?.content?.filter((workflow: workflowInfo) => workflow.workflowCode === workflowCode);

  const ResetForm = () => (
    <IconButton onClick={resetForm} className={classes.iconButton}>
      <X />
    </IconButton>
  );

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <DetailedTable
          key="Profile"
          id={'profile'}
          title="Profile"
          columns={columns}
          data={profileConfigList?.getProfileList?.content}
          tableActions={profileActions}
          showPagination={true}
          showTopPagination={true}
          rowsPerPage={10}
        />
      )}
      <Dialog
        maxWidth="xl"
        fullWidth
        open={dialogOpen}
        onClose={resetForm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        id="custom-grid-dialog"
      >
        <Grid container style={{ backgroundColor: 'rgb(240,242,250)' }}>
          <Grid item xs={2}>
            <span style={{ position: 'fixed' }}>
              {Object.keys(_.groupBy(dataConfig, 'group')).map((item: any, index: any) => (
                <ScrollIntoView selector={`#${item}`}>
                  <Button
                    data-id="add-config"
                    color={index === selected ? 'primary' : 'default'}
                    onClick={() => setSelected(index)}
                    className={`${classes.moduleBtn} ${index === selected ? 'active' : ''}`}
                  >
                    {item}
                  </Button>
                </ScrollIntoView>
              ))}
            </span>
          </Grid>
          <Grid item xs={10}>
            <AddProfile
              classes={classes}
              rowData={rowData}
              participantCode={participantCode}
              error={error}
              handleChangeWorkflow={handleChangeWorkflow}
              handleChangeSubService={handleChangeSubService}
              isEdit={isEdit}
              workflowList={workflowList}
              workflowCode={workflowCode}
              subService={subService}
              handleProfileSubmit={handleProfileSubmit}
              dataConfig={dataConfig}
              getLatestValues={getLatestValues}
              ResetForm={ResetForm}
            />
          </Grid>
        </Grid>
      </Dialog>
      <DialogForm
        openFn={isDeleteOpen}
        dataConfig={[]}
        handleDialogClose={() => setIsDeleteOpen(!isDeleteOpen)}
        handleDialogSubmit={deleteProfileConfig}
        submitlabel={'Delete'}
        title={`Delete Profile`}
        dialogId="delete-field-id"
        closeFn={resetForm}
        maxWidth="sm"
      >
        <Typography color="textPrimary">Are you sure want to delete ?</Typography>
      </DialogForm>
    </>
  );
};

export default Profile;
